import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ClinicService from '../../../services/clinic.service';
import {toast} from 'react-toastify';
import Loading from '../../common/Loading';

const ClinicDashboard = () => {
    const navigate = useNavigate();
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(1);
    const [clinicsPerPage] = useState(12);
    const [copiedItem, setCopiedItem] = useState(null);

    // Function to copy text to clipboard
    const copyToClipboard = (text, label, id, type) => {
        navigator.clipboard.writeText(text);
        setCopiedItem({ id, type }); // type can be 'phone', 'email', or 'number'
        setTimeout(() => setCopiedItem(null), 2000);
    };

    useEffect(() => {
        setLoading(true);
        ClinicService.getAllClinics()
            .then(res => {
                setClinics(res.data || []);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
            });
    }, []);

    // Get filtered clinics based on search and type filter
    const getFilteredClinics = () => {
        return clinics
            .filter(clinic =>
                typeFilter === "ALL" || clinic.clinicType === typeFilter
            )
            .filter(clinic =>
                clinic.name.includes(searchTerm) ||
                clinic.email?.includes(searchTerm) ||
                clinic.phone?.includes(searchTerm) ||
                clinic.clinicType?.includes(searchTerm) ||
                clinic.number?.toString().includes(searchTerm) // Add number to search
            )
            .sort((a, b) => (b.status === "ON") - (a.status === "ON"));
    };

    // Count clinics by type
    const countClinicsByType = (type) => {
        return clinics.filter(clinic => clinic.clinicType === type).length;
    };

    const handleClick = (id) => {
        navigate(`/clinics/${id}`);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleTypeFilterChange = (e) => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    const clinicCard = (clinic) => (
        <div className="col-md-3 mb-4" key={clinic.id}>
            <div className="break"></div>
            <div
                className={`card h-100 ${clinic.status === "OFF" ? "bg-light" : "border-primary"}`}
                style={{
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s',
                    cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = ''}
                onClick={() => handleClick(clinic.id)}
            >
                <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="card-title mb-0 text-primary">{clinic.name}</h6>
                        <span className={`badge ${clinic.status === "ON" ? "bg-success" : "bg-secondary"}`}>
                            {clinic.status}
                        </span>
                    </div>

                    {/* Add number field with badge and copy button */}
                    {clinic.number && (
                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="badge service-status status-completed text-success fw-bold">
                                    <i className="bi bi-hash me-1"></i>
                                    {clinic.number}
                                </span>
                                <button
                                    className="btn btn-sm btn-outline-secondary py-0 px-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(clinic.number.toString(), "Clinic number", clinic.id, 'number');
                                    }}
                                    title="Copy clinic number"
                                >
                                    {copiedItem?.id === clinic.id && copiedItem?.type === 'number' ? (
                                        <i className="bi bi-check2"></i>
                                    ) : (
                                        <i className="bi bi-copy"></i>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {clinic.clinicType && (
                        <div className="mb-1">
                            <small className="text-muted">
                                <i className="bi bi-tag-fill me-2"></i>
                                {clinic.clinicType}
                            </small>
                        </div>
                    )}

                    <div className="mb-1">
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                <i className="bi bi-telephone me-2"></i>
                                {clinic.phone}
                            </small>
                            <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(clinic.phone, "Phone number", clinic.id, 'phone');
                                }}
                                title="Copy phone number"
                            >
                                {copiedItem?.id === clinic.id && copiedItem?.type === 'phone' ? (
                                    <i className="bi bi-check2"></i>
                                ) : (
                                    <i className="bi bi-copy"></i>
                                )}
                            </button>
                        </div>
                    </div>

                    {clinic.dateStarted && (
                        <div className="mb-1">
                            <small className="text-muted">
                                <i className="bi bi-calendar-check me-2"></i> Since&nbsp;
                                {clinic.dateStarted}
                            </small>
                        </div>
                    )}

                    <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                <i className="bi bi-envelope me-2"></i>
                                {clinic.email}
                            </small>
                            <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(clinic.email, "Email address", clinic.id, 'email');
                                }}
                                title="Copy email"
                            >
                                {copiedItem?.id === clinic.id && copiedItem?.type === 'email' ? (
                                    <i className="bi bi-check2"></i>
                                ) : (
                                    <i className="bi bi-copy"></i>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderClinics = () => {
        const filteredClinics = getFilteredClinics();

        // Pagination logic
        const indexOfLastClinic = currentPage * clinicsPerPage;
        const indexOfFirstClinic = indexOfLastClinic - clinicsPerPage;
        const currentClinics = filteredClinics.slice(indexOfFirstClinic, indexOfLastClinic);
        const totalPages = Math.ceil(filteredClinics.length / clinicsPerPage);

        return (
            <>
                <div className="row">
                    {currentClinics.length > 0 ?
                        currentClinics.map(clinic => clinicCard(clinic)) :
                        <div className="col-12 text-center py-5">
                            <h5 className="text-muted">No clinics found matching your criteria</h5>
                        </div>
                    }
                </div>

                {filteredClinics.length > clinicsPerPage && (
                    <div className="row mt-4">
                        <div className="col-12 d-flex justify-content-center">
                            <nav>
                                <ul className="pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </button>
                                    </li>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                        <li
                                            key={number}
                                            className={`page-item ${currentPage === number ? 'active' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(number)}
                                            >
                                                {number}
                                            </button>
                                        </li>
                                    ))}

                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="container-fluid py-4">
            <section className="section dashboard">
                <div className="row mb-4 align-items-center">
                    <div className="col-md-6">
                        <div className="d-flex flex-wrap justify-content-start align-items-center gap-3">
                            <div className="d-flex align-items-center">
                                <span className="badge bg-primary rounded-pill me-2">
                                    {clinics.length}
                                </span>
                                <span>Total Clinics</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-info rounded-pill me-2">
                                    {countClinicsByType("ADMIN")}
                                </span>
                                <span>Admin</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-success rounded-pill me-2">
                                    {countClinicsByType("EYECONX")}
                                </span>
                                <span>EyeconX</span>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-warning rounded-pill me-2">
                                    {countClinicsByType("SMS_GATEWAY")}
                                </span>
                                <span>SMS Gateway</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="row g-2">
                            <div className="col-md-9">
                                <div className="input-group">
                                    <span className="input-group-text bg-white">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search clinics..."
                                        onChange={handleSearchChange}
                                        value={searchTerm}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={handleTypeFilterChange}
                                >
                                    <option value="ALL">All Types</option>
                                    <option value="ADMIN">Admin</option>
                                    <option value="EYECONX">EyeconX</option>
                                    <option value="SMS_GATEWAY">SMS Gateway</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    {typeFilter === "ALL" ? "All Clinics" : `${typeFilter} Clinics`}
                                </h5>
                                <span className="badge bg-primary">
                                    Showing {getFilteredClinics().length} clinics
                                </span>
                            </div>
                            <div className="card-body">
                                {loading ? <Loading /> : renderClinics()}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ClinicDashboard;